import { useTranslation } from 'react-i18next';
import { GUILD_XP_GUILDMASTER, GUILD_XP_JOURNEYMAN, GUILD_XP_MASTER, formatDate } from '@pk/shared';
import type { Id } from '@pk/shared';
import { Collapsible, Empty, Tag } from '../../components/ui';
import { IconChevron } from '../../components/icons';
import type { CityRow } from './MarketsView';

/** Next rank threshold for a rank number (null at the top). */
export const nextGuildThreshold = (rank: number): number | null =>
  rank === 0 ? GUILD_XP_JOURNEYMAN : rank === 1 ? GUILD_XP_MASTER : rank === 2 ? GUILD_XP_GUILDMASTER : null;

/** Cities where the player has guild xp first (rank, then xp, high to low), then the rest nearest first. */
export function sortGuildCities(cities: CityRow[]): CityRow[] {
  const near = (c: CityRow) => c.roadDays ?? 999;
  return [...cities].sort((a, b) => {
    const ax = a.myGuildXp ?? 0;
    const bx = b.myGuildXp ?? 0;
    if ((ax > 0) !== (bx > 0)) return ax > 0 ? -1 : 1;
    if (ax > 0) return (b.myGuildRank ?? 0) - (a.myGuildRank ?? 0) || bx - ax || near(a) - near(b);
    return near(a) - near(b);
  });
}

/** First-class guild overview: every city with your xp, rank, progress to the next rank and the next election. */
export function GuildsView({ cities, onOpen }: { cities: CityRow[]; onOpen: (id: Id) => void }) {
  const { t } = useTranslation();
  if (cities.length === 0) return <Empty>{t('guilds.empty')}</Empty>;
  const rows = sortGuildCities(cities);
  const anyXp = rows.some((c) => (c.myGuildXp ?? 0) > 0);
  const card = (c: CityRow) => {
    const xp = c.myGuildXp ?? 0;
    const rank = c.myGuildRank ?? 0;
    const next = nextGuildThreshold(rank);
    return (
      <li key={c.cityId} className="tr-guild-card">
        <button type="button" className="tr-guild-open" onClick={() => onOpen(c.cityId)} aria-label={`${c.name}: ${t('guilds.open')}`}>
          <span className="tr-guild-head">
            <span className="tr-guild-name">{c.name}</span>
            {rank >= 1 && <Tag tone={rank >= 3 ? 'gold' : 'green'}>{t(`trade.guild.rank.${rank}`)}</Tag>}
            <span className="tr-guild-xp num">{xp > 0 ? t('guilds.xp', { xp }) : t('guilds.none')}</span>
          </span>
          <span className="bar tr-guild-bar" role="progressbar" aria-valuenow={xp} aria-valuemax={next ?? xp}>
            <span className="bar-fill gold" style={{ width: `${next == null ? 100 : Math.min(100, (xp / next) * 100)}%` }} />
          </span>
          <span className="tr-guild-sub">
            <span>{next != null ? t('eco.guild.toNext', { xp, next, rank: t(`trade.guild.rank.${rank + 1}`) }) : t('eco.guild.top')}</span>
            <span>{c.nextElectionDay != null ? t('guilds.election', { date: formatDate(c.nextElectionDay) }) : t('guilds.noElection')}</span>
          </span>
          <span className="tr-guild-go">{t('guilds.open')}<IconChevron className="tr-chev" /></span>
        </button>
      </li>
    );
  };
  const mine = rows.filter((c) => (c.myGuildXp ?? 0) > 0);
  const rest = rows.filter((c) => (c.myGuildXp ?? 0) === 0);
  return (
    <section className="tr-section">
      <p className="tr-help">{t('guilds.help')}</p>
      <Collapsible title={t('guilds.howTitle')} defaultOpen={!anyXp} className="tr-guild-how">
        <p className="tr-help">{t('guilds.earn')}</p>
        <ul className="tr-guild-ranks">
          <li>{t('guilds.journeyman', { xp: GUILD_XP_JOURNEYMAN })}</li>
          <li>{t('guilds.master', { xp: GUILD_XP_MASTER })}</li>
          <li>{t('guilds.guildmaster', { xp: GUILD_XP_GUILDMASTER })}</li>
        </ul>
      </Collapsible>
      {mine.length > 0 && (<>
        <h4 className="tr-h">{t('guilds.yours')}<span className="tr-count">{mine.length}</span></h4>
        <ul className="tr-guild-list">{mine.map(card)}</ul>
      </>)}
      {rest.length > 0 && (<>
        <h4 className="tr-h">{mine.length > 0 ? t('guilds.others') : t('trade.view.guilds')}</h4>
        <ul className="tr-guild-list">{rest.map(card)}</ul>
      </>)}
    </section>
  );
}
